// @ts-nocheck
// authService.ts
import { useAppState } from './stores/AppState'
import { UserManager, User, WebStorageStateStore, Log } from 'oidc-client'
import localStorageService from '@/stores/localStorageService'
import axios from 'axios'
import router from './router'
import { checkLoginAndHandleSplashScreen } from './utils/splashScreen' // Import the showSplashScreen function

//Log.logger = console
//Log.level = Log.DEBUG

let userManager
let config

const initializeAuth = async () => {
  const store = useAppState()
  const discovery = await store.getDiscovery
  const localStore = localStorageService.getStore('state')
  const loco = window.location
  const appRootUrl = `${loco.protocol}//${loco.host}${import.meta.env.BASE_URL}`
  let identityUrl = discovery.identitySettings.identityUrl
  const clientId = discovery.identitySettings.clientId

  if (!identityUrl.endsWith('/')) {
    identityUrl += '/'
  }

  config = {
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    authority: identityUrl,
    client_id: clientId,
    redirect_uri: `${appRootUrl}auth/signinwin/${'main'}`,
    popup_post_logout_redirect_uri: `${'main'}auth/signoutpop/${'main'}`,
    popup_redirect_uri: `${appRootUrl}auth/signinpop/${'main'}`,
    silent_redirect_uri: `${appRootUrl}auth/signinsilent/${'main'}`,
    post_logout_redirect_uri: `${appRootUrl}`,
    response_type: 'code',
    scope: 'openid profile email policy offline_access api.access.recruitment.portal',
    prefix: 'oidc',
    loadUserInfo: true
  }

  userManager = new UserManager(config)
  userManager.events.addUserLoaded((user: User) => {
    localStore.setItem('user', user)
    localStore.setItem('emailAddress', user.profile.email)
    router.push({ name: 'home' })
  })
}

let isAuthenticating = false
let redirectAttempts = 0 // Counter for redirect attempts

export async function signIn() {
  if (redirectAttempts > 3) {
    return
  }

  redirectAttempts++

  if (isAuthenticating) {
    return
  }
  isAuthenticating = true

  const user = await userManager.getUser()
  if (!user) {
    try {
      const response = await userManager.signinRedirect()
      logMessage('signinRedirect response:', response)
    } catch (error) {
      logMessage('Error during signinRedirect:', error)
    }
    const user = await userManager.getUser()
    if (user) {
      logMessage('Access token:', user.access_token)
    }
  } else {
    logMessage('Access token:', user.access_token)
  }
}

export async function signInSilent() {
  try {
    logMessage('Signing in silently...')

    return await userManager.signinSilent()
  } catch (error) {
    logMessage('Error in signInSilent():', error)
    await userManager.signinRedirect()
  }
}

export async function signOut() {
  return userManager.signoutRedirect()
}

export async function getUser() {
  return userManager.getUser()
}

export async function handleCallback() {
  try {
    if (!userManager) {
      throw new Error('userManager is not initialized')
    }

    const response = await userManager.signinRedirectCallback()
    logMessage('signinRedirectCallback response:', response)
    if (response?.profile) {
      logMessage(`Window signin callback success`, response.profile.email)

      await checkLoginAndHandleSplashScreen()
    }
  } catch (error) {
    if (error.message === 'login_required') {
      logMessage('login_required: Redirecting to login')

      clearOidcState()
      logMessage('Cleared OIDC state from local storage')
      await userManager.signinRedirect()
    } else if (error.message === 'No matching state found in storage') {
      logMessage(
        'Error handling authentication callback: No matching state found in storage. This may be due to an invalid or expired state parameter.'
      )
      localStorage.removeItem('oidc.state')
      await userManager.signinRedirect()
    } else {
      logMessage('Error handling authentication callback:', error.message)
    }
  }
}

function logMessage(message: string) {
  const logs = JSON.parse(localStorage.getItem('authLogs') || '[]')
  logs.push({ timestamp: new Date().toISOString(), message })
  localStorage.setItem('authLogs', JSON.stringify(logs))
}

function clearOidcState() {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('oidc.')) {
      localStorage.removeItem(key)
    }
  })
}

export async function isAuthenticated() {
  const user = await getUser()
  if (user !== null) {
    //Check user expiry
    if (user.expired) {
      //Try to sign in silently
      console.log(user.refresh_token)
      getAccessToken()
    }
  }
  return user !== null && !user.expired
}

export async function getAccessToken() {
  const user = await getUser()
  if (user) {
    const diff = -10 //Time in minutes before expiry to refresh token
    const expiryTime = new Date(user.expires_at * 1000)
    const cutOffTime = new Date(expiryTime.getTime() + diff * 60000)
    const currentTime = new Date()
    if (user.expired || currentTime > cutOffTime) {
      const url = `${config.authority}connect/token`
      const data = `client_id=${config.client_id}&grant_type=refresh_token&refresh_token=${encodeURIComponent(user.refresh_token)}`
      try {
        const refresh = await axios.post(url, data)
        user.access_token = refresh.data.access_token
        user.expires_at = refresh.data.expires_in + Math.floor(Date.now() / 1000)
        userManager.storeUser(user)

        // Refresh the page to get the new token
        window.location.reload()
        return user.access_token
      } catch (error) {
        logMessage('Error refreshing token:', error)
        await signOut()
        return null
      }
    } else {
      return user.access_token
    }
  }
}

export { initializeAuth }
